import type { IconGlyph } from '@sunwebgroup/uds-ui/icon'
import { Icon } from '@sunwebgroup/uds-ui/icon'
import { PaymentMethodIcon } from '@sunwebgroup/uds-ui/payment-method-icon'
import cn from 'classnames'
import { buildImgSrc } from '@/helpers/image-url'
import { useIntl } from '@/intl'
import { mapPaymentMethodToGlyph } from '@/scopes/common/domain/payment-methods/payment-methods'
import { shouldShowFooterWebsiteAward } from '@/scopes/common/domain/policies/siteName'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { useEnv } from '@/scopes/common/infrastructure/env'
import { Block, Header } from './Blocks'
import { FooterList as List, FooterListItem as ListItem } from './List'
import styles from './PartnersBar.module.css'
import { WebsiteOfTheYearAward } from './WebsiteOfTheYearAward'
import { WorldSkiAward } from './WorldSkiAward'

export default function PartnersBar() {
  const intl = useIntl()
  const mediaServerBaseUrl = useEnv('MEDIA_SERVER_BASE_URL')
  const links = useConfiguration('links')
  const paymentMethods = useConfiguration('paymentMethods')
  const brandName = useConfiguration('brandName')
  const affiliates = useConfiguration('affiliates')
  const otherPartners = useConfiguration('otherPartners')
  const siteName = useConfiguration('siteName')
  const shouldShowWebsiteAward = shouldShowFooterWebsiteAward(siteName, new Date())
  const socialMediaLinks: Array<{ id: string, url?: string, iconGlyph: IconGlyph }> = [
    { id: 'facebook', url: links.facebook, iconGlyph: 'LogoFacebook' },
    { id: 'instagram', url: links.instagram, iconGlyph: 'LogoInstagram' },
    { id: 'pinterest', url: links.pinterest, iconGlyph: 'LogoPinterest' },
    { id: 'tiktok', url: links.tiktok, iconGlyph: 'LogoTiktok' },
    { id: 'youtube', url: links.youtube, iconGlyph: 'LogoYoutube' },
  ]

  return (
    <div className={styles.wrapper}>
      <section className={styles.footerBottomContent}>
        <section>
          <Header
            className={styles.socialsBlockHeader}
          >
            {intl.formatMessage({ id: 'footer_us_find_on_social_media' })}
          </Header>
          <List direction="x" className={styles.socialsBlockList}>
            {socialMediaLinks
              .filter(({ url }) => Boolean(url))
              .map(({ id, url, iconGlyph }) => (
                <ListItem key={id} href={url!} title={id}>
                  <Icon glyph={iconGlyph} className={styles.socialIcon} />
                </ListItem>
              ))}
          </List>
        </section>
        {paymentMethods && (
          <Block>
            <Header>{intl.formatMessage({ id: 'footer_payment' })}</Header>
            <List direction="x" spacing="var(--primitives-space-16)">
              {paymentMethods.map((payment, index) => (
                <ListItem key={index} href={payment.link} alt={payment.altText}>
                  <PaymentMethodIcon className={styles.logo} glyph={mapPaymentMethodToGlyph(payment.method, brandName)} size={32} />
                </ListItem>
              ))}
            </List>
          </Block>
        )}
        <Block>
          <Header>{intl.formatMessage({ id: 'footer_affiliated_with' })}</Header>
          <List direction="x" spacing="var(--primitives-space-16)">
            {affiliates.map((affiliate, index) => (
              <ListItem key={index} href={affiliate.link}>
                <img
                  className={styles.logo}
                  src={buildImgSrc(`${affiliate.image}`, 85)}
                  alt={affiliate.altText}
                  loading="lazy"
                />
              </ListItem>
            ))}
          </List>
        </Block>
        <Block>
          <Header>{intl.formatMessage({ id: 'footer_sustainability' })}</Header>
          <List direction="x" spacing="var(--primitives-space-16)">
            <ListItem
              href={links.travelife}
              title={intl.formatMessage({ id: 'footer_sustainability_link_title' })}
              alt={intl.formatMessage({ id: 'footer_sustainability_link_title' })}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                className={styles.logo}
                src={buildImgSrc(`/-/media/sustainability/travelife.ashx`, 85)}
                alt="sustainability"
                loading="lazy"
              />
            </ListItem>
            {otherPartners?.map(partner => (
              <ListItem key={partner.altText} href={partner.link}>
                <img
                  className={styles.logo}
                  src={buildImgSrc(partner.image, 85)}
                  alt={partner.altText}
                  loading="lazy"
                />
              </ListItem>
            ))}
          </List>
        </Block>
      </section>
      {shouldShowWebsiteAward
        ? (
            <div className={styles.awards}>
              {links.worldSkiAwards && (
                <WorldSkiAward href={links.worldSkiAwards} className={cn(styles.award, styles.awardsSkiAward)} />
              )}
              <WebsiteOfTheYearAward className={styles.award} />
            </div>
          )
        : links.worldSkiAwards && <WorldSkiAward href={links.worldSkiAwards} className={styles.award} />}
      <Block className={styles.appLinksBlock}>
        <Block>
          <List direction="x" spacing="var(--primitives-space-4)">
            <ListItem href={links.appleStore}>
              <img
                className={styles.downloadBadge}
                src={buildImgSrc(`${mediaServerBaseUrl}/footer/download_app_store.png`, 200)}
                alt={intl.formatMessage({ id: 'footer_download_app_store' })}
                loading="lazy"
              />
            </ListItem>
            <ListItem href={links.googlePlayStore}>
              <img
                className={styles.downloadBadge}
                src={buildImgSrc(`${mediaServerBaseUrl}/footer/download_play_store.png`, 200)}
                alt={intl.formatMessage({ id: 'footer_download_google_play' })}
                loading="lazy"
              />
            </ListItem>
          </List>
        </Block>
      </Block>
    </div>
  )
}
